import type { ReactNode } from 'react';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn, getLoginUrl, reportEvent } from '@/app/utils';
import { openInNewTab, shouldOpenInNewTab } from '@/app/utils/client';
import { usePathname, useRouter } from '@/i18n/routing';
import { isAuthRequired } from '../_utils/is-auth-required';

export const MenuItem = ({
  name,
  icon,
  path,
  activePaths,
  className,
  onClick,
  badge,
  tag,
  exact = false,
  id,
  collapsed = false,
  shortName,
  reportElementName,
}: {
  name: string;
  icon?: ReactNode;
  path?: string;
  activePaths?: string[];
  badge?: ReactNode;
  className?: string;
  onClick?: () => void;
  tag?: string;
  exact?: boolean;
  id?: string;
  collapsed?: boolean;
  shortName?: string;
  reportElementName?: string;
}) => {
  const pathname = usePathname();
  const router = useRouter();
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const userInfo = useStore((state) => state.userInfo);

  const matchesPath = (targetPath: string) => pathname === targetPath || pathname.startsWith(`${targetPath}/`);

  const isActive = () => {
    const pathsToMatch = [path, ...(activePaths ?? [])].filter((item): item is string => Boolean(item));
    if (pathsToMatch.length === 0) return false;
    if (exact) {
      return pathsToMatch.some((targetPath) => pathname === targetPath);
    }
    return pathsToMatch.some((targetPath) => matchesPath(targetPath));
  };
  const active = isActive();

  const handleMenuClick = (href: string, shouldOpenNewTab = false) => {
    const menuItemName = reportElementName ?? href.split('/').slice(-1)[0];
    const isNeedLogin = isAuthRequired(href);
    const targetHref = userInfo || !isNeedLogin ? href : getLoginUrl(href);
    if (menuItemName === 'templates') {
      reportEvent('templates_exposure', {
        refer_element: 'menu - templates',
      });
    } else {
      reportEvent('element_click', {
        element_name: `menu - ${menuItemName}`,
      });
    }
    if (shouldOpenNewTab) {
      openInNewTab(targetHref);
      if (!userInfo && isNeedLogin) {
        reportEvent('login_page_exposure', {
          refer_element: `menu - ${menuItemName}`,
          refer_page: pathname,
        });
      }
      return;
    }
    if (userInfo || !isNeedLogin) {
      router.push(targetHref);
    } else {
      openLoginDialog({ redirectUrl: href });
      reportEvent('login_pop_exposure', {
        refer_element: `menu - ${menuItemName}`,
        refer_page: pathname,
      });
    }
  };

  const content = (
    <div
      key={name}
      className={cn(
        'relative flex h-11 select-none items-center rounded-10 px-3 text-system-white/40 transition-colors',
        collapsed && 'h-[52px] flex-col justify-center gap-0.5 px-0',
        active ? 'bg-system-white16 text-system-white' : 'cursor-pointer hover:bg-system-white08',
        className,
      )}
      onClick={(e) => {
        onClick?.();
        if (!path) return;
        handleMenuClick(path, shouldOpenInNewTab(e));
      }}
      id={id}
    >
      <div className="flex-shrink-0">{icon}</div>

      {!collapsed && (
        <div className={cn('flex flex-1 items-center', badge && !tag && 'pr-8')}>
          <span className="mr-2 ml-3 whitespace-pre-line font-medium">{name}</span>
          {tag ? (
            <div className="flex-1 overflow-hidden">
              <img src={tag} alt="tag" className="max-h-[22px] min-h-4 object-contain" />
            </div>
          ) : null}
        </div>
      )}
      {collapsed && (
        <span
          className={cn(
            'max-w-[56px] truncate font-medium text-[10px]',
            active ? 'text-system-white' : 'text-system-white/40',
          )}
        >
          {shortName || name}
        </span>
      )}
      {badge && !tag && (
        <div className={cn('pointer-events-none absolute z-[1]', collapsed ? 'top-1 right-1' : 'top-[7px] right-3')}>
          {badge}
        </div>
      )}
    </div>
  );

  return collapsed ? (
    <CustomTooltip content={name} side="right" sideOffset={8}>
      {content}
    </CustomTooltip>
  ) : (
    content
  );
};
